import routes from '@config/routes';
import tevoRoutes from '@config/tevoRoutes';
import { apiFetcher, serverFetcher } from '@instance';
import { BaseProperty } from '@interfaces/expedia/property';
import { useBuildPackagesStore } from '@store/build-package-store';
import { useSearchHotelStore } from '@store/search-hotel-store';
import { toHotelDistanceMiles } from '@v2/views/SearchHotel/hotel-distance';
import { useSearchParams } from 'next/navigation';
import { useMemo, useState } from 'react';
import useSWR from 'swr';
import { EventLoaderResponse } from './use-event-loader';
import useMinimumPrice from './useMinimumPrice';

interface UseBuildPackagePricesProps {
  ticketsCount?: number;
  selectedHotelPrice?: number | null;
  selectedTicketPrice?: number | null;
  isBundle?: boolean;
}

export function useBuildPackagePrices({
  ticketsCount = 2,
  selectedHotelPrice = null,
  selectedTicketPrice = null,
  isBundle = true,
}: UseBuildPackagePricesProps = {}): {
  /** The minimum price for tickets based on selection or available tickets */
  ticketPrice: number;
  /** The minimum price for hotels based on selection or available properties */
  hotelPrice: number;
  /** The total combined price of tickets and hotel */
  totalPrice: number;
  /** Loading state for data fetching and price calculations */
  isLoading: boolean;
  /** Error message if something goes wrong */
  error: string | null;
} {
  const searchParams = useSearchParams();
  const eventId = searchParams?.get('eventId');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Get hotel filters from store
  const distance = useSearchHotelStore((state) =>
    toHotelDistanceMiles(state.hotelFilters.distance)
  );

  // Get base hotel search params from store
  const startDate = useBuildPackagesStore((state) => state.hotelStartDate);
  const endDate = useBuildPackagesStore((state) => state.hotelEndDate);
  const location = useBuildPackagesStore((state) => state.location);
  const roomConfigurations = useBuildPackagesStore(
    (state) => state.roomConfigurations
  );
  const base = useMemo(
    () => ({
      startDate,
      endDate,
      location,
      roomConfigurations,
    }),
    [endDate, location, roomConfigurations, startDate]
  );
  const canSearchHotels = Boolean(
    base.startDate &&
    base.endDate &&
    base.location &&
    base.roomConfigurations.length > 0
  );

  // Fetch tickets data
  const { data: ticketData, isLoading: isLoadingTickets } =
    useSWR<EventLoaderResponse>(
      eventId ? tevoRoutes.getEventProxy(eventId, true) : null,
      apiFetcher,
      {
        dedupingInterval: 60_000,
        refreshInterval: 120_000,
        revalidateIfStale: true,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
      }
    );

  // Fetch hotels
  const { data: hotelsData, isValidating: isLoadingHotels } = useSWR<{
    error: [];
    properties: BaseProperty[];
  }>(
    isBundle && canSearchHotels
      ? [
          routes.propertySearch,
          base.endDate,
          base.startDate,
          base.location,
          base.roomConfigurations,
          distance,
          'distance-low-high',
          isBundle,
        ]
      : null,
    (val) =>
      serverFetcher(val[0], {
        body: {
          base,
          filters: {
            distance,
            sortBy: 'distance-low-high',
          },
          options: {
            requiredCurrency: 'USD',
            maxResult: 100,
            isBundle,
          },
        },
      }),
    {
      dedupingInterval: 60_000,
      refreshInterval: 120_000,
      revalidateIfStale: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  // Use minimum price hook for price calculations
  const { ticketPrice, hotelPrice, priceLoading } = useMinimumPrice({
    hotels: hotelsData?.properties || [],
    tickets: ticketData?.[1]?.ticket_groups || [],
    ticketsCount,
    selectedHotelPrice,
    selectedTicketPrice,
  });

  // Calculate total price
  const totalPrice = ticketPrice + hotelPrice;

  return {
    ticketPrice,
    hotelPrice,
    totalPrice,
    isLoading: isLoading || isLoadingHotels || isLoadingTickets || priceLoading,
    error,
  };
}

export default useBuildPackagePrices;
